import React, { PureComponent } from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
  WithTheme,
  withTheme,
  Theme,
} from '@lifeomic/chroma-react/styles';
import SurveyItem from '../util/SurveyItem';
import clsx from 'clsx';

// Caps for the opt-in field-width extension. The container needs an explicit
// width so the cap binds both as a page child and as a formLine flex child.
const FIELD_WIDTH_CAPS: Record<'xs' | 'sm' | 'md' | 'lg', string> = {
  xs: '8rem',
  sm: '12rem',
  md: '18rem',
  lg: '26rem',
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      maxWidth: '100%',
      display: 'flex',
    },
    cappedWidth: {
      width: '100%',
      // A capped item has to be allowed below its content's min-content width,
      // or controls that refuse to shrink (selects especially) keep their
      // natural size and overflow the cap, escaping the section or the
      // repeating-group instance card. Scoped to the cap so uncapped items
      // across other surveys keep their existing sizing.
      minWidth: 0,
      // chroma's TextField hard-codes min-width: 180px on both the input and
      // its container, which is wider than the xs cap and matches sm. Without
      // relaxing it the input renders at 180px regardless of the cap and slides
      // out from under it, colliding with the unit suffix beside it. The
      // container nests at different depths with and without a unit suffix, so
      // relax every descendant rather than guessing the level. The doubled
      // selector is required: a single class ties with chroma's own class and
      // loses or wins purely on JSS injection order.
      '&& *': {
        minWidth: 0,
      },
    },
    margins: {
      margin: theme.spacing(3, 0),
    },
    denseMargins: {
      // Dense questions still need enough separation for each label to read
      // with the control below it rather than the preceding control.
      margin: theme.spacing(1, 0),
    },
    indented: {
      marginLeft: theme.spacing(8),
    },
    denseIndented: {
      marginLeft: theme.spacing(5),
    },
  });

export interface SurveyItemContainerOwnProps {
  children: any;
  surveyItem: SurveyItem;
}

export type SurveyItemContainerProps = SurveyItemContainerOwnProps &
  WithStyles<typeof styles> &
  WithTheme;

class SurveyItemContainer extends PureComponent<SurveyItemContainerProps> {
  render() {
    const { surveyItem, classes, theme } = this.props;

    const fieldWidth = surveyItem.fieldWidth;
    const widthCap = fieldWidth ? FIELD_WIDTH_CAPS[fieldWidth] : undefined;

    // Table row details supply their own indent/well; extra container
    // margins would float the field away from its trigger row.
    if (surveyItem.isTableRowDetail) {
      return (
        <div
          className={clsx(classes.root, widthCap && classes.cappedWidth)}
          style={widthCap ? { maxWidth: widthCap } : undefined}
        >
          {this.props.children}
        </div>
      );
    }

    const isDense = surveyItem.hasDenseMargins;
    const isIndented = surveyItem.isIndented;

    const classNames = [
      classes.root,
      isDense ? classes.denseMargins : classes.margins,
      widthCap ? classes.cappedWidth : undefined,
    ].filter(Boolean);

    const indentationCount =
      (isDense ? 3 : 0) + (isIndented ? surveyItem.indentationCount : 0);

    const marginLeft = theme.spacing(indentationCount);

    return (
      <div
        className={clsx(...classNames)}
        style={{ marginLeft, ...(widthCap ? { maxWidth: widthCap } : {}) }}
      >
        {this.props.children}
      </div>
    );
  }
}

export default withTheme(withStyles(styles)(SurveyItemContainer));
